import React, { useState, useEffect } from 'react';
import API from '../lib/api';
import {
    Activity, CheckCircle, XCircle, Zap, Clock, TrendingUp, AlertTriangle,
    ChevronDown, ChevronUp, Server, Cpu, BarChart3
} from 'lucide-react';
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
    RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
    PieChart, Pie
} from 'recharts';

const ENGINE_LABELS = {
    elo_rating: 'Elo Rating', team_strength: 'Team Strength v4', monte_carlo: 'Monte Carlo',
    recent_form: 'Recent Form', head_to_head: 'H2H Historical', venue_intelligence: 'Venue Intel',
    player_impact: 'Player Impact', batting_index: 'Batting Index', bowling_index: 'Bowling Index',
    external_consensus: 'External Consensus', social_sentiment: 'Social Sentiment',
    google_trends: 'Google Trends', community_wisdom: 'Community Wisdom',
    matchup_analysis: 'Matchup Analysis', toss_scenario_sim: 'Toss Scenario',
    historical_win_rate: 'Historical WR', fantasy_xi_strength: 'Fantasy XI',
    impact_player_edge: 'Impact Player', season_preview: 'Season Preview',
    post_toss_intelligence: 'Post-Toss Intel', gpt_analysis: 'GPT-5.2',
};

const CATEGORY_COLORS = {
    Statistical: '#06B6D4', Simulation: '#8B5CF6', Form: '#F59E0B',
    Historical: '#EC4899', Venue: '#10B981', Player: '#3B82F6',
    Analytics: '#F97316', 'External Intelligence': '#EF4444', Intelligence: '#14B8A6',
    Matchup: '#A855F7', AI: '#6366F1', Other: '#6B7280',
};

const STEP_LABELS = {
    lineup: 'Lineup Fetch', cache_invalidate: 'Cache Invalidate',
    intelligence_scrape: 'Intel Scrape', engine_run: 'Engine Run',
    god_mode: 'God Mode', save_predictions: 'Save Predictions',
    fantasy_freeze: 'Fantasy Freeze',
};

function StatCard({ label, value, sub, icon: Icon, color = 'cyan', testId }) {
    const colorMap = {
        cyan: 'text-cyan-400 border-cyan-500/30', green: 'text-green-400 border-green-500/30',
        amber: 'text-amber-400 border-amber-500/30', red: 'text-red-400 border-red-500/30',
        purple: 'text-purple-400 border-purple-500/30',
    };
    return (
        <div className={`glass-card p-5 border-l-2 ${colorMap[color]}`} data-testid={testId}>
            <div className="flex items-center justify-between mb-2">
                <span className="tech-mono text-[10px] text-white/30 tracking-widest">{label}</span>
                {Icon && <Icon className={`w-4 h-4 ${colorMap[color].split(' ')[0]}`} />}
            </div>
            <div className={`heading-primary text-3xl ${colorMap[color].split(' ')[0]}`}>{value}</div>
            {sub && <div className="text-xs text-white/30 mt-1">{sub}</div>}
        </div>
    );
}

function StepHealthBar({ stepStats }) {
    const steps = Object.entries(stepStats);
    return (
        <div className="glass-card p-6" data-testid="step-health">
            <h3 className="heading-primary text-base text-white mb-4 flex items-center gap-2">
                <Server className="w-4 h-4 text-cyan-400" /> PIPELINE STEP HEALTH
            </h3>
            <div className="space-y-3">
                {steps.map(([key, stat]) => (
                    <div key={key} className="flex items-center gap-3">
                        <span className="tech-mono text-[10px] text-white/40 w-28 shrink-0 truncate">{STEP_LABELS[key] || key}</span>
                        <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
                            <div
                                className="h-full rounded-full transition-all duration-700"
                                style={{
                                    width: `${stat.success_rate}%`,
                                    background: stat.success_rate >= 90 ? '#10B981' : stat.success_rate >= 70 ? '#F59E0B' : '#EF4444',
                                }}
                            />
                        </div>
                        <span className="tech-mono text-[10px] text-white/50 w-12 text-right">{stat.success_rate}%</span>
                        <span className="text-[10px] text-white/20 w-16 text-right">{stat.success}/{stat.total}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function EngineTable({ engines }) {
    const [sortKey, setSortKey] = useState('accuracy');
    const [sortAsc, setSortAsc] = useState(false);

    const toggleSort = (key) => {
        if (sortKey === key) setSortAsc(!sortAsc);
        else { setSortKey(key); setSortAsc(false); }
    };

    const sorted = [...engines].sort((a, b) => {
        const av = a[sortKey] ?? -1;
        const bv = b[sortKey] ?? -1;
        return sortAsc ? av - bv : bv - av;
    });

    const SortIcon = ({ k }) => {
        if (sortKey !== k) return null;
        return sortAsc ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />;
    };

    return (
        <div className="glass-card p-6 overflow-x-auto" data-testid="engine-table">
            <h3 className="heading-primary text-base text-white mb-4 flex items-center gap-2">
                <Cpu className="w-4 h-4 text-purple-400" /> ENGINE PERFORMANCE MATRIX
            </h3>
            <table className="w-full text-sm">
                <thead>
                    <tr className="border-b border-white/10">
                        <th className="text-left py-2 px-2 tech-mono text-[10px] text-white/30">ENGINE</th>
                        <th className="text-right py-2 px-2 tech-mono text-[10px] text-white/30 cursor-pointer select-none" onClick={() => toggleSort('accuracy')}>
                            <span className="flex items-center justify-end gap-1">ACCURACY <SortIcon k="accuracy" /></span>
                        </th>
                        <th className="text-right py-2 px-2 tech-mono text-[10px] text-white/30 cursor-pointer select-none" onClick={() => toggleSort('success_rate')}>
                            <span className="flex items-center justify-end gap-1">UPTIME <SortIcon k="success_rate" /></span>
                        </th>
                        <th className="text-right py-2 px-2 tech-mono text-[10px] text-white/30 cursor-pointer select-none" onClick={() => toggleSort('total_predictions')}>
                            <span className="flex items-center justify-end gap-1">RUNS <SortIcon k="total_predictions" /></span>
                        </th>
                        <th className="text-right py-2 px-2 tech-mono text-[10px] text-white/30">VERIFIED</th>
                        <th className="text-right py-2 px-2 tech-mono text-[10px] text-white/30">ERRORS</th>
                    </tr>
                </thead>
                <tbody>
                    {sorted.map((e, i) => (
                        <tr key={e.engine_key} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                            <td className="py-2.5 px-2">
                                <span className="text-white/80 text-xs">{ENGINE_LABELS[e.engine_key] || e.engine_key}</span>
                            </td>
                            <td className="py-2.5 px-2 text-right">
                                {e.accuracy !== null ? (
                                    <span className={`tech-mono text-xs font-bold ${e.accuracy >= 60 ? 'text-green-400' : e.accuracy >= 45 ? 'text-amber-400' : 'text-red-400'}`}>
                                        {e.accuracy}%
                                    </span>
                                ) : (
                                    <span className="text-white/20 text-xs">—</span>
                                )}
                            </td>
                            <td className="py-2.5 px-2 text-right">
                                <span className={`tech-mono text-xs ${e.success_rate >= 95 ? 'text-green-400' : e.success_rate >= 80 ? 'text-amber-400' : 'text-red-400'}`}>
                                    {e.success_rate}%
                                </span>
                            </td>
                            <td className="py-2.5 px-2 text-right tech-mono text-xs text-white/50">{e.total_predictions}</td>
                            <td className="py-2.5 px-2 text-right tech-mono text-xs text-white/40">
                                {e.total_verified > 0 ? `${e.correct}/${e.total_verified}` : '—'}
                            </td>
                            <td className="py-2.5 px-2 text-right">
                                {e.errors > 0 ? (
                                    <span className="tech-mono text-xs text-red-400">{e.errors}</span>
                                ) : (
                                    <span className="text-green-400/50 text-xs">0</span>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function CategoryBreakdown({ categories }) {
    const chartData = categories.map(c => ({
        name: c.category,
        engines: c.engine_count,
        accuracy: c.avg_accuracy ?? 0,
        uptime: c.avg_success_rate,
        fill: CATEGORY_COLORS[c.category] || '#6B7280',
    }));

    return (
        <div className="glass-card p-6" data-testid="category-breakdown">
            <h3 className="heading-primary text-base text-white mb-4 flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-amber-400" /> ENGINE CATEGORIES
            </h3>
            <div className="h-56">
                <ResponsiveContainer>
                    <BarChart data={chartData} layout="vertical" margin={{ left: 10, right: 20 }}>
                        <XAxis type="number" domain={[0, 100]} tick={{ fill: '#71717a', fontSize: 10 }} />
                        <YAxis type="category" dataKey="name" tick={{ fill: '#a1a1aa', fontSize: 10 }} width={110} />
                        <Tooltip
                            contentStyle={{ background: '#18181b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 12 }}
                            labelStyle={{ color: '#fff' }}
                            formatter={(v, name) => [`${v}%`, name === 'accuracy' ? 'Accuracy' : 'Uptime']}
                        />
                        <Bar dataKey="uptime" name="Uptime" radius={[0, 4, 4, 0]} barSize={14}>
                            {chartData.map((entry, i) => (
                                <Cell key={i} fill={entry.fill} fillOpacity={0.7} />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
                {categories.map(c => (
                    <div key={c.category} className="flex items-center gap-1.5 px-2 py-1 rounded bg-white/5 text-[10px]">
                        <div className="w-2 h-2 rounded-full" style={{ background: CATEGORY_COLORS[c.category] || '#6B7280' }} />
                        <span className="text-white/50">{c.category}</span>
                        <span className="text-white/30">({c.engine_count})</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function EngineRadar({ engines }) {
    const radarData = engines
        .filter(e => e.accuracy !== null)
        .slice(0, 12)
        .map(e => ({
            engine: (ENGINE_LABELS[e.engine_key] || e.engine_key).split(' ')[0],
            accuracy: e.accuracy,
            uptime: e.success_rate,
        }));

    if (radarData.length < 3) return null;

    return (
        <div className="glass-card p-6" data-testid="engine-radar">
            <h3 className="heading-primary text-base text-white mb-4 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-green-400" /> ENGINE ACCURACY RADAR
            </h3>
            <div className="h-64">
                <ResponsiveContainer>
                    <RadarChart data={radarData}>
                        <PolarGrid stroke="rgba(255,255,255,0.08)" />
                        <PolarAngleAxis dataKey="engine" tick={{ fill: '#a1a1aa', fontSize: 9 }} />
                        <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fill: '#52525b', fontSize: 8 }} />
                        <Radar name="Accuracy" dataKey="accuracy" stroke="#06B6D4" fill="#06B6D4" fillOpacity={0.15} strokeWidth={2} />
                        <Radar name="Uptime" dataKey="uptime" stroke="#8B5CF6" fill="#8B5CF6" fillOpacity={0.1} strokeWidth={1.5} />
                        <Tooltip
                            contentStyle={{ background: '#18181b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 12 }}
                        />
                    </RadarChart>
                </ResponsiveContainer>
            </div>
            <div className="flex items-center gap-4 mt-2 justify-center">
                <div className="flex items-center gap-1.5 text-[10px] text-white/40">
                    <div className="w-3 h-0.5 bg-cyan-400 rounded" /> Accuracy
                </div>
                <div className="flex items-center gap-1.5 text-[10px] text-white/40">
                    <div className="w-3 h-0.5 bg-purple-400 rounded" /> Uptime
                </div>
            </div>
        </div>
    );
}

function PipelineTimeline({ timeline }) {
    if (!timeline || timeline.length === 0) {
        return (
            <div className="glass-card p-6" data-testid="pipeline-timeline">
                <h3 className="heading-primary text-base text-white mb-4 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-cyan-400" /> EXECUTION TIMELINE
                </h3>
                <p className="text-white/30 text-sm text-center py-8">No pipeline executions yet. Trigger one from Admin Panel.</p>
            </div>
        );
    }

    return (
        <div className="glass-card p-6" data-testid="pipeline-timeline">
            <h3 className="heading-primary text-base text-white mb-4 flex items-center gap-2">
                <Clock className="w-4 h-4 text-cyan-400" /> EXECUTION TIMELINE
            </h3>
            <div className="space-y-3 max-h-[420px] overflow-y-auto pr-2 scrollbar-thin">
                {timeline.map((ev, i) => {
                    const allOk = Object.values(ev.steps).every(s => s === 'ok');
                    const hasError = Object.values(ev.steps).some(s => s === 'error');
                    return (
                        <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-white/[0.02] border border-white/5 hover:border-white/10 transition-colors">
                            <div className="mt-0.5">
                                {allOk ? (
                                    <CheckCircle className="w-4 h-4 text-green-400" />
                                ) : hasError ? (
                                    <AlertTriangle className="w-4 h-4 text-amber-400" />
                                ) : (
                                    <XCircle className="w-4 h-4 text-red-400" />
                                )}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-white/80 text-xs font-medium">{ev.home_team} vs {ev.away_team}</span>
                                    <span className="tech-mono text-[9px] px-1.5 py-0.5 rounded bg-white/5 text-white/30">{ev.match_id}</span>
                                    <span className={`tech-mono text-[9px] px-1.5 py-0.5 rounded ${ev.status === 'completed' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                                        {ev.status}
                                    </span>
                                </div>
                                <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                                    {Object.entries(ev.steps).map(([step, status]) => (
                                        <div
                                            key={step}
                                            className={`w-3 h-3 rounded-sm ${status === 'ok' ? 'bg-green-500/60' : status === 'error' ? 'bg-red-500/60' : 'bg-white/10'}`}
                                            title={`${STEP_LABELS[step] || step}: ${status}`}
                                        />
                                    ))}
                                    <span className="tech-mono text-[9px] text-white/20 ml-2">{ev.engines_count} engines</span>
                                </div>
                                {ev.started_at && (
                                    <div className="tech-mono text-[9px] text-white/15 mt-1">
                                        {new Date(ev.started_at).toLocaleString()}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default function PipelineHealth() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        API.get('/pipeline-health')
            .then(r => { setData(r.data); setLoading(false); })
            .catch(e => { setError(e.message); setLoading(false); });
    }, []);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="glass-card p-8 text-center">
                <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-3" />
                <p className="text-white/50 text-sm">Failed to load pipeline health: {error}</p>
            </div>
        );
    }

    if (!data) return null;

    const { overview, step_stats, engine_stats, categories, timeline } = data;

    return (
        <div className="space-y-6" data-testid="pipeline-health-dashboard">
            {/* Header */}
            <div className="glass-card p-6">
                <div className="flex items-center gap-3 mb-1">
                    <Activity className="w-5 h-5 text-cyan-400" />
                    <h2 className="heading-primary text-base md:text-xl text-white">PIPELINE HEALTH DASHBOARD</h2>
                    <div className="badge-ai">21 ENGINES</div>
                    <div className="relative ml-2 flex items-center gap-1.5">
                        <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                        <span className="tech-mono text-[10px] text-green-400/60">LIVE</span>
                    </div>
                </div>
                <p className="text-sm text-white/30">Real-time monitoring of the post-toss orchestration pipeline. Execution stats, per-engine accuracy, and category breakdowns.</p>
            </div>

            {/* Overview Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <StatCard
                    label="PIPELINE RUNS" value={overview.total_pipeline_runs}
                    sub={`${overview.successful_runs} successful`} icon={Zap} color="cyan"
                    testId="stat-pipeline-runs"
                />
                <StatCard
                    label="SUCCESS RATE" value={`${overview.success_rate}%`}
                    sub={`${overview.failed_runs} failed`} icon={CheckCircle}
                    color={overview.success_rate >= 90 ? 'green' : overview.success_rate >= 70 ? 'amber' : 'red'}
                    testId="stat-success-rate"
                />
                <StatCard
                    label="TOTAL ENGINES" value={overview.total_engines}
                    sub="Per pipeline run" icon={Cpu} color="purple"
                    testId="stat-total-engines"
                />
                <StatCard
                    label="GOD MODE ACCURACY"
                    value={overview.god_mode_accuracy !== null ? `${overview.god_mode_accuracy}%` : '—'}
                    sub={overview.god_mode_verified > 0 ? `${overview.god_mode_correct}/${overview.god_mode_verified} verified` : 'No verified matches'}
                    icon={TrendingUp} color="amber"
                    testId="stat-god-mode-accuracy"
                />
                <StatCard
                    label="ENGINE CATEGORIES" value={categories.length}
                    sub="Distinct groups" icon={BarChart3} color="purple"
                    testId="stat-categories"
                />
            </div>

            {/* Row: Step Health + Category Breakdown */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <StepHealthBar stepStats={step_stats} />
                <CategoryBreakdown categories={categories} />
            </div>

            {/* Engine Radar */}
            <EngineRadar engines={engine_stats} />

            {/* Engine Table */}
            <EngineTable engines={engine_stats} />

            {/* Timeline */}
            <PipelineTimeline timeline={timeline} />
        </div>
    );
}
